import { makeAutoObservable, runInAction } from 'mobx';

import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import { DriveModifierStore } from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import DriveSecurityAPI from '@/security/api/DriveSecurityAPI';
import { DRIVE_TARGET_NAME } from '@/security/components/GlobalAccessModal/DriveGlobalAccessModalStore.constants';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { DriveGlobalAccessTarget, DriveGlobalAccessType } from '@/security/types/AccessTarget';
import { ProjectDataAccess } from '@/security/types/DataAccess';

export class DriveGlobalAccessModalStore {
  access: GlobalAccessLevel;

  dataAccess: ProjectDataAccess;

  shouldInheritAccess = true;

  isUpdating = false;

  constructor(
    private readonly target: DriveGlobalAccessTarget,
    private readonly type: DriveGlobalAccessType,
    private readonly driveModifierStore: DriveModifierStore,
    private readonly driveSecurityAPI = new DriveSecurityAPI(),
  ) {
    makeAutoObservable<DriveGlobalAccessModalStore, 'target' | 'type' | 'driveModifierStore' | 'driveSecurityAPI'>(this, {
      isDataAccessEditable: false,
      isInheritAccessEditable: false,
      target: false,
      type: false,
      driveModifierStore: false,
      driveSecurityAPI: false,
    });

    this.access = target.access;
    this.dataAccess = target.dataAccess;
  }

  get isDataAccessEditable() {
    return this.type === 'driveProject';
  }

  get isInheritAccessEditable() {
    return this.type !== 'driveProject';
  }

  get accessDiff() {
    return this.target.access !== this.access;
  }

  get dataAccessDiff() {
    return this.target.dataAccess !== this.dataAccess;
  }

  get willRemovePublicAccess() {
    return this.target.access !== GlobalAccessLevel.Restricted && this.access === GlobalAccessLevel.Restricted;
  }

  setAccess = (nextAccessLevel: GlobalAccessLevel) => {
    this.access = nextAccessLevel;
  };

  setDataAccess = (nextDataAccessLevel: ProjectDataAccess) => {
    this.dataAccess = nextDataAccessLevel;
  };

  setShouldInheritAccess = (shouldInheritAccess: boolean) => {
    this.shouldInheritAccess = shouldInheritAccess;
  };

  updateAccessList = async (): Promise<void> => {
    this.isUpdating = true;

    await this.driveSecurityAPI.updateGlobalAccess(
      this.target.id,
      this.access,
      this.dataAccess,
      this.isInheritAccessEditable ? this.shouldInheritAccess : undefined,
    );

    this.driveModifierStore.notifyAccessChange(this.target.id);

    Notifier.success(`${DRIVE_TARGET_NAME[this.type]} access updated`);

    runInAction(() => {
      this.isUpdating = false;
    });
  };

  reset = () => {
    this.access = this.target.access;
    this.dataAccess = this.target.dataAccess;
    this.shouldInheritAccess = true;
  };
}

export const useDriveGlobalAccessModalStore = () => useStore(DriveGlobalAccessModalStore);
